import { create } from "zustand";

export type ToastVariant = "info" | "success" | "warning" | "danger";

/** Bouton facultatif rendu dans la notification (ex. « Annuler » après un déplacement). */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: number;
  variant: ToastVariant;
  title: string;
  body?: string;
  action?: ToastAction;
  /** Durée d'affichage, conservée pour pouvoir relancer le minuteur après une pause. */
  dismissMs: number;
}

interface ToastState {
  toasts: Toast[];
  push: (variant: ToastVariant, title: string, body?: string, action?: ToastAction) => void;
  remove: (id: number) => void;
  /** Suspend l'effacement automatique (survol ou focus clavier sur la notification). */
  hold: (id: number) => void;
  /** Relance le minuteur quand la notification n'est plus ni survolée ni focalisée. */
  release: (id: number) => void;
}

const AUTO_DISMISS_MS = 5000;
// Lire l'annonce puis viser un bouton prend plus de temps que la simple lecture :
// une notification actionnable reste affichée nettement plus longtemps.
const AUTO_DISMISS_WITH_ACTION_MS = 8000;

let counter = 0;

const timers = new Map<number, ReturnType<typeof setTimeout>>();

function disarm(id: number): void {
  const timer = timers.get(id);
  if (timer === undefined) return;
  clearTimeout(timer);
  timers.delete(id);
}

function arm(id: number, ms: number): void {
  disarm(id);
  timers.set(
    id,
    setTimeout(() => {
      timers.delete(id);
      useToastStore.getState().remove(id);
    }, ms),
  );
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (variant, title, body, action) => {
    const id = ++counter;
    const dismissMs = action ? AUTO_DISMISS_WITH_ACTION_MS : AUTO_DISMISS_MS;
    set((s) => ({ toasts: [...s.toasts, { id, variant, title, body, action, dismissMs }] }));
    arm(id, dismissMs);
  },
  remove: (id) => {
    disarm(id);
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
  hold: (id) => disarm(id),
  release: (id) => {
    // Le minuteur repart de zéro : on vient de rendre la main, pas de finir la lecture.
    const toast = get().toasts.find((t) => t.id === id);
    if (toast) arm(id, toast.dismissMs);
  },
}));
